import React, { cloneElement } from "react";
import {
  Datagrid,
  Filter,
  TextField,
  SearchInput,
  Button,
  sanitizeListRestProps,
  TopToolbar,
  useListContext,
  ResourceContextProvider,
  ShowProps,
  RowClickFunction,
  FunctionField,
} from "react-admin";
import IconPlus from "@material-ui/icons/Add";
import CustomDateField from "components/CustomDateField";
import { Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import CustomBooleanField from "components/CustomBooleanField";
import CustomEmptyComponent from "components/CustomEmptyComponent";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomPagination from "../../components/CustomPagination";

enum AcademicType {
  regularAcademic = "regular",
  ccaAcademic = "cca",
}

const AcademicYearFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const ListActions = (props: any) => {
  const {
    className,
    exporter,
    filters,
    maxResults,
    history,
    academicType,
    ...rest
  } = props;
  const { resource, displayedFilters, filterValues, showFilter } =
    useListContext();

  let actionUrl = "";
  let buttonLabel = "";

  switch (academicType) {
    case AcademicType.regularAcademic:
      actionUrl = `/academicYears/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create Regular Academic Year";
      break;
    case AcademicType.ccaAcademic:
      actionUrl = `/ccaAcademicYears/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create CCA Academic Year";
      break;
    default:
      break;
  }

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}

      <Button onClick={() => history.push(actionUrl)} label={buttonLabel}>
        <IconPlus />
      </Button>
    </TopToolbar>
  );
};

const CustomDatagrid = ({ rowClick, academicType }: any) => {
  const { data } = useListContext();

  return (
    <Datagrid data={data} rowClick={rowClick}>
      <TextField source="id" />
      <TextField source="name" label="Academic Year" />
      <CustomDateField source="startDate" emptyText="-" />
      <CustomDateField source="endDate" emptyText="-" />
      <CustomDateField source="earlyRegistrationStartDate" emptyText="-" />
      <CustomDateField source="earlyRegistrationEndDate" emptyText="-" />
      <CustomDateField source="lateRegistrationStartDate" emptyText="-" />
      {academicType === AcademicType.ccaAcademic && (
        <CustomBooleanField
          source="allowLateRegistration"
          textForTrueValue="Yes"
          textForFalseValue="No"
        />
      )}
    </Datagrid>
  );
};

const AcademicYear = (props: ShowProps) => {
  const rowClick: RowClickFunction = (id, basePath, record) =>
    `/academicYears/${id}/show?schoolId=${Number(record.schoolId)}`;
  const rowClickCca: RowClickFunction = (id, basePath, record) =>
    `/ccaAcademicYears/${id}/show?schoolId=${Number(record.schoolId)}`;

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Regular Academic Year</Typography>
      </Grid>
      <Grid item xs={12}>
        <ResourceContextProvider value="academicYears">
          <PaddingDenseStyled2List
            exporter={false}
            hasCreate={false}
            filters={<AcademicYearFilter />}
            filterDefaultValues={{ SchoolId: Number(props.id) }}
            bulkActionButtons={false}
            actions={
              <ListActions
                history={props.history}
                academicType={AcademicType.regularAcademic}
              />
            }
            title=" "
            pagination={<CustomPagination />}
            {...props}
          >
            <CustomDatagrid
              rowClick={rowClick}
              academicType={AcademicType.regularAcademic}
            />
          </PaddingDenseStyled2List>
        </ResourceContextProvider>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">CCA Academic Year</Typography>
      </Grid>
      <Grid item xs={12}>
        <FunctionField
          render={(record: any) =>
            record.isCcaSettingEnable ? (
              <ResourceContextProvider value="ccaAcademicYears">
                <PaddingDenseStyled2List
                  exporter={false}
                  hasCreate={false}
                  filters={<AcademicYearFilter />}
                  filterDefaultValues={{ SchoolId: Number(props.id) }}
                  bulkActionButtons={false}
                  actions={
                    <ListActions
                      history={props.history}
                      academicType={AcademicType.ccaAcademic}
                    />
                  }
                  title=" "
                  pagination={<CustomPagination />}
                  {...props}
                >
                  <CustomDatagrid
                    rowClick={rowClickCca}
                    academicType={AcademicType.ccaAcademic}
                  />
                </PaddingDenseStyled2List>
              </ResourceContextProvider>
            ) : (
              <CustomEmptyComponent
                resource="ccaAreas"
                title="The CCA setting is disabled"
                caption="Please enable the CCA setting in the school setting summary to show the CCA school area."
                hasCreate={false}
              />
            )
          }
        />
      </Grid>
    </SanitizedGrid>
  );
};

export default AcademicYear;
